import { Link } from "@tanstack/react-router";
import type { CountySummary, StateInfo } from "@/lib/states";
import { RISK_SCALE_EDGE_ALPHA, riskAdvice } from "@/lib/risk";
import {
  CDC_STATUSES,
  CDC_STATUS_LABEL,
  CDC_STATUS_SOURCE,
  CDC_STATUS_VAR,
  cdcStatusMeaning,
  countyHeadline,
  type CdcCountyStatus,
  type StateCountyStatuses,
} from "@/lib/county-status-text";
import { countyNoun, countyNounForCount } from "@/lib/county-label";
import { Fact, SeasonSourceLine } from "./risk-snapshot";
import { RiskBadge } from "./risk-badge";
import { SeasonNow } from "./season-now";
import { CountySourceLine } from "./county-source";
import { CountyMap } from "./county-map";
import { cn } from "@/lib/utils";

/**
 * Where a county sits on CDC's three-step record, drawn like the site's level scale:
 * every step visible, the current one taller, framed and bold.
 */
export function CdcStatusScale({
  status,
  place,
  className,
}: {
  status: CdcCountyStatus;
  place: string;
  className?: string;
}) {
  const at = CDC_STATUSES.indexOf(status);
  const edge = `color-mix(in oklab, var(--foreground) ${RISK_SCALE_EDGE_ALPHA * 100}%, transparent)`;
  return (
    <figure
      className={cn("w-full", className)}
      aria-label={`${place}: CDC record "${CDC_STATUS_LABEL[status]}", step ${at + 1} of ${CDC_STATUSES.length}.`}
    >
      <ol aria-hidden className="grid grid-cols-3 gap-1">
        {CDC_STATUSES.map((s, i) => {
          const current = i === at;
          return (
            <li key={s} className="min-w-0">
              <span className="flex h-3.5 items-end">
                <span
                  data-current={current || undefined}
                  className={cn(
                    "block w-full [print-color-adjust:exact] print:border print:border-black",
                    current ? "h-3.5 print:border-2" : "h-1.5",
                  )}
                  style={{
                    backgroundColor: CDC_STATUS_VAR[s],
                    boxShadow: `inset 0 0 0 ${current ? "1.5px var(--foreground)" : `1px ${edge}`}`,
                  }}
                />
              </span>
              <span
                className={cn(
                  "mt-2 block text-[0.6875rem] leading-tight sm:text-xs",
                  current ? "font-semibold text-foreground" : "text-muted-foreground",
                )}
              >
                {CDC_STATUS_LABEL[s]}
              </span>
            </li>
          );
        })}
      </ol>
    </figure>
  );
}

/** "118 of Texas's 254 counties are established and 66 more reported." */
function stateTally(state: StateInfo, lists: StateCountyStatuses): string | null {
  // D.C. is one county; its own record is the headline already.
  if (lists.total <= 1) return null;
  const nouns = countyNounForCount(state.abbr);
  const e = lists.established.length;
  const r = lists.reported.length;
  if (!e && !r) return `CDC has no Lone Star tick records for any of ${state.name}'s ${nouns}.`;
  const parts = [`CDC lists ${e} of ${state.name}'s ${lists.total} ${nouns} as established`];
  if (r) parts.push(`${r} more as reported`);
  return `${parts.join(" and ")}.`;
}

/**
 * The answer at the top of a county page, about the county: CDC's own record for it,
 * where that sits among its neighbours, and the state's level as context underneath.
 * It replaced the state's answer card, which made every county page open with
 * "Yes. Lone Star ticks live in Texas." whatever CDC records for the county itself.
 */
export function CountySnapshot({
  state,
  fips,
  label,
  status,
  lists,
  summary,
  context,
}: {
  state: StateInfo;
  fips: string;
  label: string;
  status: CdcCountyStatus;
  lists: StateCountyStatuses;
  /** A named rating for this county from a source other than CDC's county record. */
  summary?: CountySummary;
  /** The sentence after CDC's definition: what the record means for someone here. */
  context: string;
}) {
  const nouns = countyNounForCount(state.abbr);
  const tally = stateTally(state, lists);
  return (
    <article
      className="relative border border-t-[5px] border-border bg-card px-5 pb-6 pt-5 sm:px-8 sm:pb-8 sm:pt-7"
      style={{ borderTopColor: CDC_STATUS_VAR[status] }}
    >
      <div className="grid gap-x-10 gap-y-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,19rem)]">
        <div className="min-w-0">
          <p className="label-caps text-muted-foreground">
            CDC {countyNoun(state.abbr)} record, {CDC_STATUS_SOURCE.year}
          </p>
          <CdcStatusScale status={status} place={label} className="mt-3 max-w-xs" />

          <h2 className="mt-6 font-display text-[1.75rem] font-medium leading-[1.12] tracking-[-0.015em] text-foreground sm:text-[2.25rem]">
            {countyHeadline(label, status)}
          </h2>
          <p className="measure mt-4 text-[0.9375rem] leading-relaxed text-muted-foreground">
            {cdcStatusMeaning(status, countyNoun(state.abbr))} {context}{" "}
            <a
              href={CDC_STATUS_SOURCE.url}
              target="_blank"
              rel="noopener noreferrer"
              className="link print-no-url whitespace-nowrap"
            >
              CDC county data, {CDC_STATUS_SOURCE.year}
              <span className="sr-only"> (opens in a new tab)</span>
            </a>
          </p>

          <dl className="mt-6 grid gap-x-8 gap-y-5 sm:grid-cols-2">
            <Fact term="Peak season">
              <span className="font-display text-xl">{state.peakSeason}</span>
              {state.risk === "low" ? (
                <span className="mt-1 block text-sm text-muted-foreground">
                  Other ticks are most active April to September, CDC says.
                </span>
              ) : (
                <SeasonNow
                  peakSeason={state.peakSeason}
                  className="mt-1 text-sm text-muted-foreground"
                />
              )}
              <SeasonSourceLine state={state} />
            </Fact>
            <Fact term="What to do">{riskAdvice[state.risk]}</Fact>
            {summary && summary.source.sourceId !== CDC_STATUS_SOURCE.sourceId && (
              <Fact term="Alpha-gal cases">
                <RiskBadge level={summary.awarenessLevel} />
                <CountySourceLine source={summary.source} className="mt-1" />
              </Fact>
            )}
            <Fact term={`All of ${state.name}`}>
              <RiskBadge level={state.risk} />
              {tally && (
                <span className="mt-1 block text-sm leading-snug text-muted-foreground">
                  {tally}
                </span>
              )}
              <Link
                to="/states/$state"
                params={{ state: state.slug }}
                className="link mt-1 inline-block text-sm font-semibold"
              >
                Ticks in {state.name}
              </Link>
            </Fact>
          </dl>
        </div>

        <CountyMap
          stateAbbr={state.abbr}
          stateSlug={state.slug}
          stateName={state.name}
          fips={fips}
          label={label}
          nouns={nouns}
          lists={lists}
          year={CDC_STATUS_SOURCE.year}
          className="lg:pt-1"
        />
      </div>
    </article>
  );
}
